import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
    productOptions,
    productOptions_productOptions_items_ConfigurableProduct,
    productOptionsVariables,
} from 'src/queries/__generated__/productOptions';
import { ApolloError } from '@apollo/client';
import { getUrlKeyForProduct } from 'src/util/getUrlKeyForProduct';
import { useSearch } from 'src/hooks/router-hooks';
import { useDeps } from 'src/hooks/useDeps';

import productOptionsQuery from '../../queries/productOptions.graphql';

import { optionsAndVariants } from './utils/product-options';
import { optionsToColorSwatches, optionsToSizes, BaseVariant } from './utils/product-detail.types';
import { ProductConfigOptions } from './ProductOptions';
import { Size } from './ProductSizes';
import { Swatch } from './ProductSwatch';
import { selectVariant } from './utils/select-variant';

export interface ProductOptionsQueryProps {
    pathname: string;
}

export const ProductOptionsContext = React.createContext<{
    loading: boolean;
    error: ApolloError | undefined;
    options: ProductConfigOptions[];
    baseVariants: BaseVariant[];
    sizes: Size[];
    colors: Swatch[];
    variant: BaseVariant | undefined;
}>({
    loading: true,
    error: undefined,
    options: [],
    baseVariants: [],
    sizes: [],
    colors: [],
    variant: undefined,
});

const EMPTY_ARR = [];

export const ProductOptionsProvider: React.FC<React.PropsWithChildren<ProductOptionsQueryProps>> = React.memo(
    (props) => {
        const search = useSearch();
        const {
            data,
            loading: queryLoading,
            error,
        } = useQuery<productOptions, productOptionsVariables>(productOptionsQuery, {
            variables: { urlKey: getUrlKeyForProduct(props.pathname) },
            ssr: useDeps().env.SSR_GQL,
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
        });
        const loading = queryLoading && !data;

        const processed = useMemo(() => {
            const empty = {
                loading,
                error: error,
                options: EMPTY_ARR,
                variants: EMPTY_ARR,
                sizes: EMPTY_ARR,
                colors: EMPTY_ARR,
                variant: undefined,
                sizesMapped: EMPTY_ARR,
            };
            if (
                !data ||
                !data.productOptions ||
                !Array.isArray(data.productOptions.items) ||
                !data.productOptions.items[0] ||
                Object.keys(data.productOptions.items[0]).length <= 1
            ) {
                return empty;
            }
            const firstItem = data.productOptions.items[0];
            const { options, variants } = optionsAndVariants(
                firstItem as productOptions_productOptions_items_ConfigurableProduct,
            );
            const colors = optionsToColorSwatches(options);
            const sizes = optionsToSizes(options);
            const sizesMapped = sizes.map((size) => ({ id: size.id, value: size }));

            return { options, variants, error, loading, colors, sizes, sizesMapped };
        }, [data, error, loading]);

        /**
         * Based on the current search params, select the variant
         * This should only re-compute based on when the url changes (like the param)
         * but the data above should not
         */
        const variant = useMemo(() => {
            return selectVariant(processed.options, processed.variants, search) || undefined;
        }, [processed.options, processed.variants, search]);

        const colorIndex = variant?.attributes.find((x) => x.code === 'color')?.value_index;

        /**
         * Grab all the sizes for the given color
         */

        const { sizesForColor, baseVariants } = useMemo(() => {
            const sizesForColorNotSorted: Size[] = [];
            const baseVariants: BaseVariant[] = [];
            processed.variants.forEach((variant) => {
                if (variant.color_id === colorIndex) {
                    const match =
                        processed.sizesMapped.length > 0
                            ? processed.sizesMapped?.filter((el) => el.id === variant.size_id)
                            : [];
                    baseVariants.push(variant);
                    if (match.length > 0) {
                        sizesForColorNotSorted.push({
                            ...match[0].value,
                            simpleProductId: variant.id,
                            simpleProductSku: variant.sku,
                            hideStockSource: variant.hide_stock_source,
                        });
                    }
                }
            });

            /**
             * Keep the same order of sizes
             */
            const arrayOfSizesId = processed.sizesMapped.map((el) => el.id);
            const sizesForColor = [...sizesForColorNotSorted].sort(
                (a, b) => arrayOfSizesId.indexOf(a.id) - arrayOfSizesId.indexOf(b.id),
            );

            return { sizesForColor, baseVariants };
        }, [colorIndex, processed.variants, processed.sizesMapped]);

        return (
            <ProductOptionsContext.Provider value={{ ...processed, variant, sizes: sizesForColor, baseVariants }}>
                {props.children}
            </ProductOptionsContext.Provider>
        );
    },
);
